import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Package, MapPin, Heart, Bell, LogOut, User, Mail } from "lucide-react";
import { motion } from "framer-motion";

const ProfilePage = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-32 bg-card rounded-xl mb-6"></div>
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-card rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const menuItems = [
    { icon: Package, label: 'My Orders', description: 'View order history', onClick: () => navigate('/orders') },
    { icon: MapPin, label: 'Addresses', description: 'Manage delivery addresses', onClick: () => {} },
    { icon: Heart, label: 'Wishlist', description: 'Products you love', onClick: () => {} },
    { icon: Bell, label: 'Notifications', description: 'Manage preferences', onClick: () => {} },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-foreground mb-8">My Account</h1>

          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-white/5 rounded-xl p-6 mb-8"
            data-testid="profile-card"
          >
            <div className="flex items-center gap-6">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary/20"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-10 h-10 text-primary" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-foreground">{user.name}</h2>
                <div className="flex items-center gap-2 text-muted-foreground mt-1">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{user.email}</span>
                </div>
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full mt-2 capitalize">
                  {user.role}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Menu Grid */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={item.onClick}
                className="bg-card border border-white/5 rounded-xl p-6 flex items-start gap-4 hover:border-primary/30 transition-colors text-left"
                data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="p-3 rounded-lg bg-primary/10">
                  <item.icon className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{item.label}</p>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Admin Access */}
          {user.role === 'admin' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-primary/10 border border-primary/20 rounded-xl p-6 mb-8"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Admin Dashboard</h3>
                  <p className="text-sm text-muted-foreground mt-1">Manage products, orders, and inventory</p>
                </div>
                <Button 
                  onClick={() => navigate('/admin')}
                  className="bg-primary hover:bg-primary/90 rounded-full"
                  data-testid="admin-dashboard-btn"
                >
                  Open Dashboard
                </Button>
              </div>
            </motion.div>
          )}

          {/* Logout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Button 
              variant="outline" 
              onClick={logout}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
